import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/AuthContext";
import { Login_api } from "../../hooks/Apis";
import { FiEye, FiEyeOff, FiLock, FiUser, FiArrowRight, FiShield, FiHeart } from "react-icons/fi";

export default function LoginPage() {
  const navigate = useNavigate();
  const { user, login } = useAuth();

  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (user) navigate("/auth/dashboard");
  }, [user, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await fetch(Login_api, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier, password }),
      });

      const data = await res.json();
      setLoading(false);

      if (data.success) login(data.user);
      else setError(data.message || "Login failed");
    } catch (err) {
      console.error(err);
      setError("Portal access currently unavailable");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center relative overflow-hidden">
      {/* Decorative Hearts Background */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
        <FiHeart className="absolute top-10 left-10 text-9xl text-red-600 rotate-12" />
        <FiHeart className="absolute bottom-20 right-20 text-9xl text-red-600 -rotate-12" />
      </div>

      <div className="relative z-10 w-full max-w-sm px-4">
        {/* Foundation Branding */}
        <div className="text-center mb-10">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 rounded-2xl bg-[#F06C60] flex items-center justify-center shadow-xl shadow-red-200">
              <FiHeart className="text-white text-3xl animate-pulse" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 uppercase tracking-tight">Admin Portal</h1>
          <p className="text-gray-500 text-sm mt-2 font-medium">Shishu Hriday Heart Foundation</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-3xl p-8 shadow-2xl shadow-gray-200 border border-gray-100">
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2 ml-1">Identity</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                  <FiUser size={18} />
                </div>
                <input
                  type="text"
                  placeholder="Username or Email"
                  className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#F06C60] transition-all text-sm font-medium"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2 ml-1">Access Key</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                  <FiLock size={18} />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter Password"
                  className="w-full pl-12 pr-12 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#F06C60] transition-all text-sm font-medium"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500 transition-colors"
                >
                  {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="p-4 rounded-xl bg-red-50 border border-red-100 text-red-600 text-xs font-bold text-center">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              className="w-full py-4 bg-[#F06C60] text-white font-bold rounded-2xl shadow-lg shadow-red-200 hover:bg-red-600 active:scale-95 transition-all flex items-center justify-center gap-3"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              ) : (
                <>
                  <span>SECURE ACCESS</span>
                  <FiArrowRight className={`transition-transform duration-300 ${isHovered ? 'translate-x-1' : ''}`} />
                </>
              )}
            </button>
          </form>
        </div>

        {/* Support Link */}
        <div className="mt-10 text-center">
          <div className="flex items-center justify-center gap-2 text-gray-400 text-sm font-medium">
            <FiShield />
            <span>Restricted Access Portal</span>
          </div>
          <p className="mt-6 text-gray-400 text-xs">
            © {new Date().getFullYear()} Shishu Hriday Heart Foundation
          </p>
        </div>
      </div>
    </div>
  );
}